(() => {
  const MOBILE_QUERY = '(max-width: 1024px)';

  const getAllVideos = (root) => Array.from(root.querySelectorAll('video.video-fade__video'));

  const getActiveVideo = (root) => {
    const videos = getAllVideos(root);
    if (videos.length === 0) return null;
    if (videos.length === 1) return videos[0];

    const isMobile = window.matchMedia(MOBILE_QUERY).matches;
    const findVideo = (type) =>
      videos.find((video) => video.classList.contains(`video-fade__video--${type}`));

    if (isMobile) return findVideo('mobile') || findVideo('desktop') || videos[0];
    return findVideo('desktop') || findVideo('mobile') || videos[0];
  };

  const clamp01 = (value) => Math.min(1, Math.max(0, value));

  const updateToggleState = (root) => {
    const toggle = root.querySelector('[data-video-fade-toggle]');
    const video = getActiveVideo(root);
    const playing = Boolean(video && !video.paused && !video.ended);

    root.classList.toggle('is-playing', playing);

    if (!toggle) return;
    const playIcon = toggle.querySelector('[data-video-fade-icon-play]');
    const pauseIcon = toggle.querySelector('[data-video-fade-icon-pause]');
    if (playIcon) playIcon.hidden = playing;
    if (pauseIcon) pauseIcon.hidden = !playing;
    toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
    toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  };

  const pauseAllVideos = (root) => {
    getAllVideos(root).forEach((video) => {
      try {
        video.pause();
      } catch (error) {
        // Ignore - the element may have been removed mid-session.
      }
    });
  };

  const initVideoFade = (root) => {
    if (root.dataset.videoFadeReady === 'true') return;

    const stage = root.querySelector('[data-video-fade-stage]');
    const media = root.querySelector('[data-video-fade-media]');
    const videos = getAllVideos(root);
    if (!stage || !media || videos.length === 0) return;

    root.dataset.videoFadeReady = 'true';

    const autoplay = root.dataset.autoplayVideo === 'true';
    const toggle = root.querySelector('[data-video-fade-toggle]');
    const mobileQuery = window.matchMedia(MOBILE_QUERY);
    const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const state = {
      inView: false,
      userPaused: false, // user explicitly paused -> never auto resume
      manualPlay: false, // user explicitly requested playback (autoplay off)
    };

    let playAttempted = false;
    let ticking = false;

    const ensurePlaying = () => {
      const desired = autoplay ? !state.userPaused : state.manualPlay;
      if (!state.inView || !desired) return;

      const video = getActiveVideo(root);
      if (!video) return;
      if (!video.paused && !video.ended) {
        playAttempted = false;
        return;
      }
      if (playAttempted) return;

      playAttempted = true;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      // Liquid renders preload="none" for autoplay videos, so no bytes are
      // fetched until this moment (stage in view). Bump to auto so the
      // upcoming load() buffers properly.
      video.preload = 'auto';

      const result = video.play();
      if (result && typeof result.then === 'function') {
        result
          .then(() => {
            playAttempted = false;
          })
          .catch(() => {
            // Retry after a short delay (e.g. not ready / autoplay policy).
            window.setTimeout(() => {
              playAttempted = false;
              ensurePlaying();
            }, 1200);
          });
      } else {
        playAttempted = false;
      }
    };

    const update = () => {
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;
      const rect = stage.getBoundingClientRect();
      const height = rect.height || 0;
      const inView = rect.top < vh && rect.bottom > 0;

      if (inView !== state.inView) {
        state.inView = inView;
        if (!inView) {
          playAttempted = false;
          pauseAllVideos(root);
        }
      }

      // Scroll progress: start when the stage is ~50% visible, end when it is
      // 100% visible (for stages taller than the viewport: when it covers the
      // whole viewport).
      let progress = 0;
      if (height > 0 && vh > 0) {
        const absTop = rect.top + window.scrollY;
        let startScroll;
        let endScroll;
        if (height <= vh) {
          startScroll = absTop - vh + height * 0.5; // top at vh - height/2
          endScroll = absTop - vh + height;         // top at vh - height
        } else {
          startScroll = absTop - vh * 0.5;          // top at viewport middle
          endScroll = absTop;                       // top at viewport top
        }
        const range = endScroll - startScroll;
        progress = range > 0 ? clamp01((window.scrollY - startScroll) / range) : 0;
      }

      if (reduceMotionQuery.matches) {
        // Reduced motion: show the video fully, no scale/fade animation.
        media.style.opacity = '1';
        media.style.transform = 'none';
      } else {
        // 80% width -> 100% width while fading in.
        // Opacity never drops below 0.8 so the video stays visible.
        const scale = 0.8 + 0.2 * progress;
        media.style.opacity = (0.8 + 0.2 * progress).toFixed(4);
        media.style.transform = `scale(${scale.toFixed(4)})`;
      }

      if (progress > 0.02) {
        root.classList.add('is-visible');
      } else {
        root.classList.remove('is-visible');
      }

      ensurePlaying();
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        update();
      });
    };

    const syncByScreen = () => {
      // Desktop/mobile videos are separate files - hand playback over to the
      // video that is now visible for the current viewport.
      pauseAllVideos(root);
      playAttempted = false;
      ensurePlaying();
      requestUpdate();
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', syncByScreen);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(syncByScreen);
    }

    if (typeof reduceMotionQuery.addEventListener === 'function') {
      reduceMotionQuery.addEventListener('change', requestUpdate);
    } else if (typeof reduceMotionQuery.addListener === 'function') {
      reduceMotionQuery.addListener(requestUpdate);
    }

    videos.forEach((video) => {
      video.addEventListener('play', () => updateToggleState(root));
      video.addEventListener('pause', () => updateToggleState(root));
      video.addEventListener('ended', () => updateToggleState(root));
    });

    if (toggle) {
      toggle.addEventListener('click', () => {
        const video = getActiveVideo(root);
        const playing = Boolean(video && !video.paused && !video.ended);

        if (playing) {
          state.userPaused = true;
          state.manualPlay = false;
          playAttempted = false;
          pauseAllVideos(root);
        } else {
          state.userPaused = false;
          state.manualPlay = true;
          ensurePlaying();
        }
        updateToggleState(root);
      });
    }

    root._videoFadeCleanup = () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (typeof mobileQuery.removeEventListener === 'function') {
        mobileQuery.removeEventListener('change', syncByScreen);
      } else if (typeof mobileQuery.removeListener === 'function') {
        mobileQuery.removeListener(syncByScreen);
      }
      if (typeof reduceMotionQuery.removeEventListener === 'function') {
        reduceMotionQuery.removeEventListener('change', requestUpdate);
      } else if (typeof reduceMotionQuery.removeListener === 'function') {
        reduceMotionQuery.removeListener(requestUpdate);
      }
      pauseAllVideos(root);
      root.classList.remove('is-visible', 'is-playing');
    };

    // Apply current scroll position immediately (covers page loads that start
    // already scrolled, anchor links, theme editor, etc.).
    requestUpdate();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-video-fade]').forEach(initVideoFade);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-video-fade]').forEach((root) => {
      root._videoFadeCleanup?.();
      delete root.dataset.videoFadeReady;
    });
  });
})();